import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user came from signup flow via query parameters
  const urlParams = new URLSearchParams(location.search);
  const fromSignup = urlParams.get('from') === 'signup';

  const handleBack = () => {
    if (fromSignup) {
      // Navigate back to signup if user came from signup flow
      navigate('/signup');
    } else if (window.history.length > 1) {
      // Use browser back if there's history
      window.history.back();
    } else {
      // Fallback to home page
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Professional Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <button
              onClick={handleBack}
              className="inline-flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-200"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">{fromSignup ? 'Back' : 'Back'}</span>
              <span className="sm:hidden">Back</span>
            </button>
            <div className="text-center flex-1">
              <img 
                src="/images/noBgColor.png" 
                alt="Silas Logo" 
                className="h-6 sm:h-8 w-auto object-contain mx-auto"
              />
            </div>
            <div className="w-12 sm:w-16"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">Terms & Conditions – Silas</h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            Welcome to Silas, an AI-powered men's mental health support platform. By accessing or using Silas, you agree to these Terms & Conditions. Please read them carefully.
          </p>
          <p className="mt-2 text-xs sm:text-sm text-gray-500">
            Last Updated: August 19, 2025
          </p>
        </div>

                 {/* Content Grid */}
         <div className="bg-white rounded-lg sm:rounded-2xl shadow-xl border border-gray-200 overflow-hidden mx-1 sm:mx-0">
           <div className="px-4 py-6 sm:px-8 sm:py-12 lg:px-12 lg:py-16 prose prose-sm sm:prose-lg max-w-none">
            <section className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">1. Purpose of Silas</h2>
              <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                Silas is designed to provide general wellness, mental health support resources, and AI-driven conversation tools. Silas is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider with any questions you may have regarding mental health, medical conditions, or well-being.
              </p>
            </section>

            <section className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">2. Emergency & Crisis Situations</h2>
              <div className="bg-red-50 border-l-4 border-red-400 p-3 sm:p-4 mb-4 rounded-r-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <p className="text-xs sm:text-sm text-red-700 font-medium leading-tight">
                      <strong>CRISIS WARNING:</strong> If you are in crisis, thinking about suicide, or considering harming yourself or others: Do not rely on Silas.
                    </p>
                  </div>
                </div>
              </div>
              <ul className="list-disc pl-4 sm:pl-6 text-gray-700 space-y-2 text-sm sm:text-base">
                <li>Call 911 (in the U.S.), go to the nearest emergency department, or contact your local emergency services immediately.</li>
                <li>You may also call or text 988 in the United States to connect with the Suicide & Crisis Lifeline, or seek equivalent crisis services in your country.</li>
              </ul>
            </section>

            <section className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">3. Disclaimer of Liability</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 mb-4 rounded-r-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <p className="text-xs sm:text-sm text-yellow-700 font-medium leading-tight">
                      <strong>Important:</strong> You acknowledge and agree that Silas is not responsible for monitoring, preventing, or responding to self-harm, suicidal actions, or mental health crises.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                Silas and its affiliates, owners, employees, and partners expressly disclaim any liability for injury, self-harm, suicide, or death arising directly or indirectly from the use of Silas.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                Use of Silas is at your own discretion and risk.
              </p>
            </section>

            <section className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">4. No Medical or Professional Advice</h2>
              <ul className="list-disc pl-4 sm:pl-6 text-gray-700 space-y-2 text-sm sm:text-base">
                <li>Information provided through Silas is for informational and educational purposes only.</li>
                <li>Silas does not provide medical, psychiatric, psychological, or therapeutic advice.</li>
                <li>Reliance on any information or interactions within Silas is solely at your own risk.</li>
              </ul>
            </section>

            <section className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">5. User Responsibility</h2>
              <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">By using Silas, you agree:</p>
              <ul className="list-disc pl-4 sm:pl-6 text-gray-700 space-y-2 text-sm sm:text-base">
                <li>You are solely responsible for your own decisions, actions, and well-being.</li>
                <li>You will not hold Silas, its owners, employees, or affiliates liable for any outcomes related to your mental health, personal decisions, or actions.</li>
              </ul>
            </section>

            <section className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">6. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                To the fullest extent permitted by law:
              </p>
              <ul className="list-disc pl-4 sm:pl-6 text-gray-700 space-y-2 mb-4 text-sm sm:text-base">
                <li>Silas shall not be liable for any direct, indirect, incidental, consequential, special, or punitive damages, including but not limited to those resulting from self-harm, suicide, or reliance on information provided.</li>
                <li>This limitation applies regardless of whether the alleged liability is based on contract, tort, negligence, strict liability, or any other legal theory.</li>
              </ul>
            </section>

            <section className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">7. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                By creating an account, accessing, or using Silas, you confirm that you have read, understood, and agreed to these Terms & Conditions, including the disclaimers of responsibility regarding self-harm and suicide.
              </p>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 sm:mt-12 px-4">
          <p className="text-xs sm:text-sm text-gray-500">
            © {new Date().getFullYear()} Silas. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 